import { isArray } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import {
  useIssuerPortfolioBrokersInfiniteListQuery,
  useIssuerPortfolioBrokersListQuery,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { SelectPaginate, SelectPaginateOption } from 'ui';
import { goodFormatNumberCustom } from 'ui/utils';
import { BrokerExtended, CurrencyEnum } from 'api';

type baseProps = {
  clearable?: boolean;
  label?: string;
  formFieldTitle?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  withBorder?: boolean;
  registerId?: number;
  placeholder?: string;
  portfolioId: number;
  date_to?: string;
  hideWithoutBalances?: boolean;
  hideWithoutCurrency?: CurrencyEnum;
  warning?: string;
  hintLabel?: string;
};

type singleProps = {
  isMulti?: false;
  onChange: (options: SelectPaginateOption<BrokerExtended> | null, fieldName: string) => void;
  value?: number;
};

type multiProps = {
  isMulti?: true;
  onChange: (options: SelectPaginateOption<BrokerExtended>[] | null, fieldName: string) => void;
  value?: number[];
};

type SelectPortfolioBrokersProps = baseProps & (singleProps | multiProps);

export const SelectPortfolioBrokers = ({
  error,
  label,
  isMulti = false,
  disabled,
  clearable,
  className,
  withBorder = false,
  value,
  formFieldTitle = '',
  onChange,
  registerId,
  placeholder,
  portfolioId,
  date_to,
  hideWithoutBalances = false,
  hideWithoutCurrency,
  warning,
  hintLabel,
}: SelectPortfolioBrokersProps) => {
  const [params] = useSearchParams();
  const register_id = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || 0);
  const { fetchNextPage } = useIssuerPortfolioBrokersInfiniteListQuery(
    {
      page: 1,
      page_size: 10,
      register_id: registerId || register_id,
      portfolio_id: portfolioId,
      date_to: date_to || undefined,
    },
    true,
  );
  const { data: dataBrokers } = useIssuerPortfolioBrokersListQuery({
    page: 1,
    page_size: 50,
    register_id: registerId || register_id,
    portfolio_id: portfolioId,
    date_to: date_to || undefined,
  });

  const allTransactions = useMemo<SelectPaginateOption<BrokerExtended>[]>(() => {
    return (dataBrokers?.data || [])
      ?.filter(item =>
        hideWithoutCurrency
          ? item.balances?.map(item => item.currency)?.includes(hideWithoutCurrency)
          : !hideWithoutBalances
          ? true
          : !!item.balances,
      )
      ?.map(item => {
        const { name, balances } = item;

        return {
          label: (
            <span className="flex gap-4 items-center justify-between font-[Inter] text-sm pr-3">
              <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                <span className="text-gray-900">{name}</span>
              </span>
              {balances && (
                <span className="text-gray-500 text-xs font-[Inter]">
                  Balances:{' '}
                  {balances
                    ?.map(({ currency, value }) =>
                      goodFormatNumberCustom(value, currency.toUpperCase(), 0, 2),
                    )
                    .join(', ')}
                </span>
              )}
            </span>
          ),
          value: item,
          toString: () => name,
        };
      });
  }, [dataBrokers?.data, hideWithoutBalances, hideWithoutCurrency]);

  const valueWithLabel = useMemo<
    SelectPaginateOption<BrokerExtended> | SelectPaginateOption<BrokerExtended>[] | null | undefined
  >(() => {
    if (!value) return null;

    const options = isArray(value)
      ? allTransactions.filter(option => value?.includes(option.value.id))
      : allTransactions.find(option => option.value.id === value);

    return options || null;
  }, [allTransactions, value]);

  const handleAsyncLoadFormOptions = useCallback(
    async (page: number) => {
      const dataBrokers = await fetchNextPage({ pageParam: page });

      const brokers = dataBrokers?.data?.pages?.at(-1)?.data || [];
      return brokers
        .filter(item =>
          hideWithoutCurrency
            ? item.balances?.map(item => item.currency)?.includes(hideWithoutCurrency)
            : !hideWithoutBalances
            ? true
            : !!item.balances,
        )
        .map(item => {
          const { name, balances } = item;

          return {
            label: (
              <span className="flex gap-4 items-center justify-between font-[Inter] text-sm pr-3">
                <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                  <span className="text-gray-900">{name}</span>
                </span>
                {balances && (
                  <span className="text-gray-500 text-xs font-[Inter]">
                    Balances:{' '}
                    {balances
                      ?.map(({ currency, value }) =>
                        goodFormatNumberCustom(value, currency.toUpperCase(), 0, 2),
                      )
                      .join(', ')}
                  </span>
                )}
              </span>
            ),
            value: item,
            toString: () => name,
          };
        });
    },
    [fetchNextPage, hideWithoutBalances, hideWithoutCurrency],
  );

  return (
    <SelectPaginate
      cacheUniqs={[
        label,
        registerId || register_id,
        portfolioId,
        date_to,
        hideWithoutBalances,
        hideWithoutCurrency,
      ]}
      isClearable={clearable}
      warning={warning}
      disabled={disabled}
      isMulti={isMulti}
      label={label}
      className={className}
      onAsync={handleAsyncLoadFormOptions}
      onChange={options => onChange(options as any, formFieldTitle)}
      value={valueWithLabel}
      error={error}
      withBorder={withBorder}
      placeholder={placeholder}
      hintLabel={hintLabel}
    />
  );
};
